import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  Bell, 
  Heart, 
  MessageCircle, 
  Calendar, 
  User, 
  Check, 
  X,
  Clock,
  Trash2
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API_V1 = `${API_BASE}/api/v1`;

const AgencyNotifications = ({ agenziaId }) => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread, likes, comments, interviews
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('agenzia_token') ||
      localStorage.getItem('agency_token');

    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      if (!Object.keys(headers).length) {
        setLoading(false);
        setError('Token di autenticazione mancante');
        toast.error('Effettua nuovamente il login per visualizzare le notifiche');
        return;
      }

      const response = await fetch(`${API_V1}/agenzie/${agenziaId}/notifications`, {
        headers
      });

      if (response.status === 401 || response.status === 403) {
        throw new Error('Accesso non autorizzato. Effettua nuovamente il login.');
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err.message);
      toast.error('Errore nel caricamento delle notifiche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (agenziaId) {
      loadNotifications();
    }
  }, [agenziaId]);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const notificationDate = new Date(dateString);
    if (Number.isNaN(notificationDate.getTime())) return '';
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return "Ora";
    if (diffInMinutes < 60) return `${diffInMinutes}m fa`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h fa`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}g fa`;
  };

  const getNotificationIcon = (tipo) => {
    switch (tipo) {
      case 'like':
        return <Heart className="w-4 h-4 text-red-500" />;
      case 'comment':
        return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'interview_request':
        return <Calendar className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getNotificationColor = (tipo) => {
    switch (tipo) {
      case 'like':
        return 'bg-red-50 border-red-100';
      case 'comment':
        return 'bg-blue-50 border-blue-100';
      case 'interview_request':
        return 'bg-green-50 border-green-100';
      default:
        return 'bg-gray-50 border-gray-100';
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };

      if (!headers.Authorization) {
        toast.error('Accesso non autorizzato');
        return;
      }

      const response = await fetch(
        `${API_V1}/agenzie/${agenziaId}/notifications/${notificationId}/read`,
        { method: 'PUT', headers }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const updatedNotification = data.notification || null;

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId
            ? updatedNotification ?? { ...notif, letta: true, data_lettura: new Date().toISOString() }
            : notif
        )
      );

      toast.success('Notifica contrassegnata come letta');
    } catch (err) {
      console.error('Error marking notification as read:', err);
      toast.error('Errore durante l\'aggiornamento della notifica');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };

      if (!headers.Authorization) {
        toast.error('Accesso non autorizzato');
        return;
      }

      const response = await fetch(
        `${API_V1}/agenzie/${agenziaId}/notifications/${notificationId}`,
        { method: 'DELETE', headers }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setNotifications((prev) => prev.filter((notif) => notif.id !== notificationId));
      toast.success('Notifica eliminata');
    } catch (err) {
      console.error('Error deleting notification:', err);
      toast.error('Errore durante l\'eliminazione della notifica');
    }
  };

  const markAllAsRead = async () => {
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };

      if (!headers.Authorization) {
        toast.error('Accesso non autorizzato');
        return;
      }

      const response = await fetch(
        `${API_V1}/agenzie/${agenziaId}/notifications/mark-all-read`,
        { method: 'PUT', headers }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, letta: true, data_lettura: new Date().toISOString() }))
      );

      const data = await response.json().catch(() => null);
      toast.success(data?.message || 'Tutte le notifiche contrassegnate come lette');
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      toast.error('Errore durante l\'aggiornamento delle notifiche');
    }
  };

  const unreadCount = notifications.filter((n) => !n.letta).length;

  const filteredNotifications = notifications.filter(notif => {
    switch (filter) {
      case 'unread':
        return !notif.letta;
      case 'likes':
        return notif.tipo === 'like';
      case 'comments':
        return notif.tipo === 'comment';
      case 'interviews':
        return notif.tipo === 'interview_request';
      default:
        return true;
    }
  });

  const getDisplayName = (notification) =>
    notification.metadata?.candidato_nome ||
    notification.metadata?.nome ||
    notification.entita_nome ||
    'Nominativo non disponibile';

  const getAvatarLabel = (notification) => {
    const base = getDisplayName(notification);
    return base
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase() || '??';
  };

  const getComment = (notification) =>
    notification.metadata?.commento ?? notification.commento;

  const getDetails = (notification) =>
    notification.metadata?.dettagli ?? notification.dettagli ?? notification.metadata?.data_colloquio;

  const getPostTitle = (notification) =>
    notification.metadata?.post_titolo ?? notification.post_titolo;

  const getMessage = (notification) =>
    notification.messaggio ?? notification.metadata?.messaggio ?? '';

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <Card key={index} className="animate-pulse">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-500" />
              Notifiche
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white">
                  {unreadCount}
                </Badge>
              )}
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
              >
                <Check className="w-4 h-4 mr-1" />
                Segna tutte come lette
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadNotifications}
                disabled={loading}
              >
                Aggiorna
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Filtri */}
        <CardContent className="border-t border-gray-100 pt-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              Tutte ({notifications.length})
            </Button>
            <Button
              variant={filter === 'unread' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('unread')}
            >
              Non lette ({unreadCount})
            </Button>
            <Button
              variant={filter === 'likes' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('likes')}
            >
              <Heart className="w-3 h-3 mr-1" />
              Mi piace
            </Button>
            <Button
              variant={filter === 'comments' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('comments')}
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              Commenti
            </Button>
            <Button
              variant={filter === 'interviews' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('interviews')}
            >
              <Calendar className="w-3 h-3 mr-1" />
              Colloqui
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && notifications.length === 0 && (
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="text-sm text-red-600">
            Impossibile caricare le notifiche: {error}
          </CardContent>
        </Card>
      )}

      {/* Lista Notifiche */}
      <div className="space-y-3">
        {filteredNotifications.map((notification) => {
          const comment = getComment(notification);
          const details = getDetails(notification);
          const postTitle = getPostTitle(notification);
          const message = getMessage(notification);
          const displayName = getDisplayName(notification);

          return (
            <Card 
              key={notification.id} 
              className={`transition-all hover:shadow-md ${
                !notification.letta ? 'ring-2 ring-blue-100 ' + getNotificationColor(notification.tipo) : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {getAvatarLabel(notification)}
                  </div>
                  
                  {/* Contenuto */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getNotificationIcon(notification.tipo)}
                          <span className="font-semibold text-gray-900">
                            {displayName}
                          </span>
                          {message && (
                            <span className="text-sm text-gray-500">
                              {message}
                            </span>
                          )}
                        </div>
                        
                        {comment && (
                          <div className="bg-gray-50 p-2 rounded mt-2">
                            <p className="text-sm text-gray-700 italic">
                              "{comment}"
                            </p>
                          </div>
                        )}
                        
                        {details && (
                          <div className="bg-green-50 p-2 rounded mt-2">
                            <p className="text-sm text-green-700">
                              📅 {details}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(notification.data_creazione)}
                          </span>
                          {postTitle && (
                            <span className="text-xs text-gray-500">
                              Post: "{postTitle}"
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Azioni */}
                      <div className="flex gap-1 ml-2">
                        {!notification.letta && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteNotification(notification.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Pulsanti azione per richieste colloquio */}
                    {notification.tipo === 'interview_request' && (
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Accetta
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Rifiuta
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                        >
                          <MessageCircle className="w-3 h-3 mr-1" />
                          Rispondi
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredNotifications.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' ? 'Nessuna notifica' : `Nessuna notifica ${filter}`}
            </h3>
            <p className="text-gray-500">
              {error ? 'Impossibile caricare le notifiche in questo momento.' : 'Le notifiche per interazioni con i tuoi post appariranno qui'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgencyNotifications;